import { useEffect, useRef, useState } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { StanButton, StanInput, StanModal } from '@/components';
import { copyToClipboard } from '@/lib/helpers';
import { useUser } from '@/queries/useUser';
import { genFunnelURL } from '../../../logic';
import { useUpdateFunnelDetails } from '../../../queries/useFunnelMutations';
import { type EditFunnelDetailsFormData, editFunnelDetailsSchema } from '../../../validations/funnelDetails';
import IcCopy from '@/assets/icons/ic-copy.svg';
import IcCheck from '@/assets/icons/ic-check.svg';
import { createIconElement } from '@/lib';

interface EditFunnelDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    storeId: number;
    currentName: string;
    currentSlug: string;
}

/**
 * Modal for editing funnel name and slug.
 * Matches Vue EditFunnelDetailsModal.vue.
 */
export const EditFunnelDetailsModal = ({
    isOpen,
    onClose,
    storeId,
    currentName,
    currentSlug,
}: EditFunnelDetailsModalProps) => {
    const updateFunnel = useUpdateFunnelDetails();
    const { user } = useUser();
    const username = user?.username ?? '';

    const form = useForm<EditFunnelDetailsFormData>({
        resolver: zodResolver(editFunnelDetailsSchema),
        defaultValues: {
            name: currentName,
            slug: currentSlug,
        },
    });

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
    } = form;

    const slug = useWatch({ control, name: 'slug' }) ?? '';

    // Ensure there's a slash between the domain and the {username}/{slug} path
    const ensureDomainSlash = (url: string) => {
        const marker = `${username}/${slug}`;
        const idx = url.lastIndexOf(marker);
        if (idx <= 0) return url;
        const before = url.slice(0, idx);
        if (before.endsWith('/')) return url;
        return `${before}/${marker}`;
    };

    const previewUrl = ensureDomainSlash(genFunnelURL(slug, username, true));
    const baseUrl = previewUrl.slice(0, previewUrl.length - slug.length);

    const [copied, setCopied] = useState(false);
    const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
        };
    }, []);

    // Reset form when modal opens with new values
    useEffect(() => {
        if (isOpen) {
            reset({ name: currentName, slug: currentSlug });
        }
    }, [isOpen, currentName, currentSlug, reset]);

    const onSubmit = async (data: EditFunnelDetailsFormData) => {
        await updateFunnel.mutateAsync({
            store_id: storeId,
            name: data.name,
            slug: data.slug,
            type: 'funnel',
        });
        onClose();
    };

    const handleCopyUrl = () => {
        const fullUrl = ensureDomainSlash(genFunnelURL(slug, username, false));
        copyToClipboard(fullUrl, { successMessage: 'URL copied!' });
        setCopied(true);
        if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
        copyTimerRef.current = setTimeout(() => setCopied(false), 3000);
    };

    const sanitizeSlug = (value: string) => value.replace(/\s+/g, '_');

    return (
        <StanModal id='edit-funnel-details-modal' isOpen={isOpen} onClose={onClose} title='Edit Funnel Details'>
            <FormProvider {...form}>
                <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-4'>
                    <Controller
                        name='name'
                        control={control}
                        render={({ field }) => (
                            <StanInput
                                id='funnel-name'
                                name={field.name}
                                topLabel='Funnel Name'
                                placeholder='Enter funnel name'
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                error={!!errors.name}
                                hintText={errors.name?.message}
                            />
                        )}
                    />
                    <div className='flex flex-col gap-2'>
                        <Controller
                            name='slug'
                            control={control}
                            render={({ field }) => (
                                <StanInput
                                    id='funnel-slug'
                                    name={field.name}
                                    topLabel='Funnel URL slug'
                                    placeholder='Enter funnel slug'
                                    value={field.value}
                                    onChange={e => field.onChange(sanitizeSlug(e.target.value))}
                                    onBlur={field.onBlur}
                                    error={!!errors.slug}
                                    hintText={errors.slug?.message || 'Letters, numbers, and underscores only.'}
                                />
                            )}
                        />
                        <div className='bg-brand-subtle border-brand-main flex items-center gap-2 rounded-md border px-3 py-2'>
                            <span className='text-body-xs text-gray shrink-0 font-semibold uppercase'>URL</span>
                            <span className='text-body-sm text-dark min-w-0 flex-1 truncate'>
                                {baseUrl}
                                <span className='text-brand-main font-semibold'>{slug}</span>
                            </span>
                            <button
                                type='button'
                                onClick={handleCopyUrl}
                                aria-label='Copy funnel URL'
                                title={copied ? 'Copied!' : 'Copy URL'}
                                className='text-brand-main focus-visible:ring-brand-main flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-sm transition-colors outline-none focus-visible:ring-2 focus-visible:ring-offset-2'
                            >
                                {copied
                                    ? createIconElement(IcCheck, { className: 'size-5 text-positive' })
                                    : createIconElement(IcCopy, { className: 'size-5 text-brand-dark' })}
                            </button>
                        </div>
                    </div>
                    <div className='flex justify-end gap-3 pt-2'>
                        <StanButton name='cancel-edit-funnel' variant='outline' onClick={onClose} type='button'>
                            Cancel
                        </StanButton>
                        <StanButton
                            name='save-edit-funnel'
                            type='submit'
                            disabled={isSubmitting}
                            loading={isSubmitting}
                        >
                            Save
                        </StanButton>
                    </div>
                </form>
            </FormProvider>
        </StanModal>
    );
};
